<template>
  <div>
    <h2>1. Import settings</h2>

    <wrapper class="mb-5">
      <div>
        <grafokett-input
          :value="importName"
          :maxlength="150"
          labelPlaceholder="Import Name"
          @input="importNameChanged"
        ></grafokett-input>
        <h3 v-if="!groups" class="text-lg">Loading groups...</h3>
        <h3 v-if="groups" class="text-lg">Select groups</h3>
        <i v-if="groups && groups.length == 0"
          >You do not have access to any groups that allow imports</i
        >
        <div
          class="flex pt-2"
          v-for="(g, i) in groups"
          :key="'targetGroup' + i"
        >
          <checkbox
            class="m-1"
            :value="g.selected"
            @input="g.selected = !g.selected"
          />
          <div class="cursor-pointer" @click="g.selected = !g.selected">
            <span class="block pl-5"
              >{{ g.groupName }}
              <small v-if="g.parentName"
                >(Parent group: {{ g.parentName }})</small
              ></span
            >
          </div>
        </div>

        <AuthorizationZone allowAny="AccountType:Administrator">
          <hr />
          <div v-if="importConfiguration">
            <h3 class="text-lg">Option for administrators</h3>
            <div class="flex pt-2">
              <checkbox
                class="m-1"
                :value="importOptions.readGroupsFromImportFile"
                @input="
                  importOptions.readGroupsFromImportFile = !importOptions.readGroupsFromImportFile
                "
              />
              <div
                class="cursor-pointer"
                @click="
                  importOptions.readGroupsFromImportFile = !importOptions.readGroupsFromImportFile
                "
              >
                <span class="block pl-5">Read groups from import file</span>
              </div>
            </div>

            <div class="mt-6">
              <span>Import Configuration</span>

              <drop-down
                v-if="importConfigurations && importConfigurations.length > 1"
                placeholder="Select file encoding"
                value-property="name"
                :options="importConfigurations"
                :value="importConfiguration"
                @input="importConfigurationChanged"
              ></drop-down>
            </div>

            <div v-if="importConfiguration.usesOrders">
              <h4 class="text-md">Order options</h4>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="importOptions.orderStrategy == 'SkipExisting'"
                  @input="importOptions.orderStrategy = 'SkipExisting'"
                />
                <div
                  class="cursor-pointer"
                  @click="importOptions.orderStrategy = 'SkipExisting'"
                >
                  <span class="block pl-5">Skip existing</span>
                </div>
              </div>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="importOptions.orderStrategy == 'ReplaceExisting'"
                  @input="importOptions.orderStrategy = 'ReplaceExisting'"
                />
                <div
                  class="cursor-pointer"
                  @click="importOptions.orderStrategy = 'ReplaceExisting'"
                >
                  <span class="block pl-5">Replace existing</span>
                </div>
              </div>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="importOptions.orderStrategy == 'MergeWithExisting'"
                  @input="importOptions.orderStrategy = 'MergeWithExisting'"
                />
                <div
                  class="cursor-pointer"
                  @click="importOptions.orderStrategy = 'MergeWithExisting'"
                >
                  <span class="block pl-5">Merge with existing</span>
                </div>
              </div>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="importOptions.orderStrategy == 'AppendWithUniqueName'"
                  @input="importOptions.orderStrategy = 'AppendWithUniqueName'"
                />
                <div
                  class="cursor-pointer"
                  @click="importOptions.orderStrategy = 'AppendWithUniqueName'"
                >
                  <span class="block pl-5">Create with unique name</span>
                </div>
              </div>
            </div>

            <div v-if="!importConfiguration.usesOrders">
              <h4 class="text-md">Article options</h4>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="importOptions.articleStrategy == 'SkipExisting'"
                  @input="importOptions.articleStrategy = 'SkipExisting'"
                />
                <div
                  class="cursor-pointer"
                  @click="importOptions.articleStrategy = 'SkipExisting'"
                >
                  <span class="block pl-5">Skip existing</span>
                </div>
              </div>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="importOptions.articleStrategy == 'ReplaceExisting'"
                  @input="importOptions.articleStrategy = 'ReplaceExisting'"
                />
                <div
                  class="cursor-pointer"
                  @click="importOptions.articleStrategy = 'ReplaceExisting'"
                >
                  <span class="block pl-5">Replace existing</span>
                </div>
              </div>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="importOptions.articleStrategy == 'MergeWithExisting'"
                  @input="importOptions.articleStrategy = 'MergeWithExisting'"
                />
                <div
                  class="cursor-pointer"
                  @click="importOptions.articleStrategy = 'MergeWithExisting'"
                >
                  <span class="block pl-5">Merge with existing</span>
                </div>
              </div>
              <div class="flex pt-2">
                <checkbox
                  class="m-1"
                  :value="
                    importOptions.articleStrategy == 'AppendWithUniqueName'
                  "
                  @input="
                    importOptions.articleStrategy = 'AppendWithUniqueName'
                  "
                />
                <div
                  class="cursor-pointer"
                  @click="
                    importOptions.articleStrategy = 'AppendWithUniqueName'
                  "
                >
                  <span class="block pl-5">Create with unique name</span>
                </div>
              </div>
            </div>

            <h4 class="text-md">Group options</h4>
            <div class="flex pt-2">
              <checkbox
                class="m-1"
                :value="importOptions.groupStrategy == 'MergeWithExisting'"
                @input="importOptions.groupStrategy = 'MergeWithExisting'"
              />
              <div
                class="cursor-pointer"
                @click="importOptions.groupStrategy = 'MergeWithExisting'"
              >
                <span class="block pl-5">Merge with existing</span>
              </div>
            </div>
            <div class="flex pt-2">
              <checkbox
                class="m-1"
                :value="importOptions.groupStrategy == 'ReplaceExisting'"
                @input="importOptions.groupStrategy = 'ReplaceExisting'"
              />
              <div
                class="cursor-pointer"
                @click="importOptions.groupStrategy = 'ReplaceExisting'"
              >
                <span class="block pl-5">Replace existing</span>
              </div>
            </div>
          </div>
        </AuthorizationZone>
      </div>
    </wrapper>

    <wrapper class="mb-4" v-if="allowedToUpload">
      <div>
        <div class="relative py-40" v-if="!file">
          <div class="absolute inset-0 flex flex-col p-4">
            <p class="mt-0">Upload file (.csv,.txt)</p>
            <Dropzone
              :multiple="false"
              @filesAdded="onFilesAdded"
              accept=".csv,.txt"
            >
              Add file
            </Dropzone>
          </div>
        </div>
        <div class="p-4 rounded bg-primary-light" v-else>
          <span>{{ file.name }}</span>
          <grafokett-button
            class="float-right small"
            @click="
              () => {
                data = {};
                onFilesAdded(undefined);
              }
            "
            >Change file</grafokett-button
          >
        </div>
      </div>
    </wrapper>

    <wrapper class="mb-4" v-if="allowedToUpload && file">
      <div class="flex flex-col w-72" v-if="fileEncodings">
        <drop-down
          class="flex-1 mr-2"
          v-if="fileEncodings.length > 0"
          placeholder="Select file encoding"
          value-property="name"
          :value="fileEncoding"
          :options="fileEncodings"
          @input="fileEncodingChanged"
        >
        </drop-down>
      </div>
    </wrapper>
    <grafokett-button
      class="flex items-center w-40 mt-4 ml-auto button primary"
      @click="validateImport(importOptions)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-6 h-6 mr-2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1"
          d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z"
        />
      </svg>
      <span class="flex-1 inline-block p-1 text-white">Validate</span>
    </grafokett-button>
  </div>
</template>

<script>
import Dropzone from "./Dropzone";
import Checkbox from "./forms/Checkbox";
import DropDown from "./forms/DropDown";
import AuthorizationZone from "./AuthorizationZone";
import GrafokettButton from "./forms/Button";
import GrafokettInput from "./forms/Input.vue";

export default {
  components: {
    Dropzone,
    Checkbox,
    DropDown,
    AuthorizationZone,
    GrafokettButton,
    GrafokettInput,
  },
  props: {
    groups: Array,
    importConfiguration: Object,
    allowedToUpload: Boolean,
    file: File,
    onFilesAdded: Function,
    validateImport: Function,
    importConfigurations: Array,
    importConfigurationChanged: Function,
    fileEncodings: Array,
    fileEncodingChanged: Function,
    fileEncoding: Object,
    importName: String,
    importNameChanged: Function,
  },
  data() {
    return {
      importOptions: {
        readGroupsFromImportFile: false,
        orderStrategy: "SkipExisting",
        articleStrategy: "SkipExisting",
        groupStrategy: "MergeWithExisting",
      },
    };
  },
};
</script>

<style></style>
